// react
import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

// components
import { Button } from '@/components/atoms/Button/Button';

import { EditorFields } from '@/components/organisms/EditorFields/EditorFields';
import { FieldDef } from '@/components/organisms/EditorFields/fieldHelper';

export type ValidationType =
  | 'min-length'
  | 'max-length'
  | 'min'
  | 'max'
  | 'password'
  | 'email'
  | 'before'
  | 'after'
  | 'before-or-on'
  | 'after-or-on'
  | 'nhs-number'
  | 'national-insurance-number'
  | 'name'
  | 'phone'
  | 'postcode'
  | 'sjp-partner-code'
  | 'disallowed-domains';

export type Validation = {
  type: ValidationType;
  value?: string | string[] | number | boolean | Date;
};

/**
 * validationAsString converts a validation array to a string.
 */
export const validationAsString = (
  validations: Validation[] | undefined,
): string | undefined => {
  if (!validations) {
    return undefined;
  }

  const validationStrings = validations.map((validation) => {
    let valueString: string;

    switch (typeof validation.value) {
      case 'string':
        valueString = validation.value;
        break;
      case 'number':
        valueString = validation.value.toString();
        break;
      case 'boolean':
        valueString = validation.value ? 'true' : 'false';
        break;
      case 'object':
        if (Array.isArray(validation.value)) {
          valueString = validation.value.join(',');
          break;
        }
        valueString = validation.value === null ? 'null' : '';
        break;
      case 'undefined':
        valueString = '';
        break;
      default:
        throw new Error('Unsupported validation value type');
    }

    return `${validation.type}:${valueString}`;
  });

  return validationStrings.join(',');
};

/**
 * Editor enables users to input information which will be updated in the
 * database.
 */
export const Editor = ({
  onSave,
  onCancel,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  isWithArrows = false,
  isActionsDisabled = false,
  value,
  onChange,
  fields,
  isDisabled = false,
  isFloatingActions = true,
  isForm = true,
  isSaveDisabled = false,
}: EditorProps) => {
  // methods
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onSave?.();
    },
    [onSave],
  );

  const EditorComponent = useMemo(
    () => (
      <>
        <EditorFields
          fields={fields}
          value={value}
          onChange={onChange}
          isDisabled={isDisabled}
        />
        {isFloatingActions && <ActionButtonsPlaceholder />}
        {!isActionsDisabled && (
          <EditorActions $isFloatingActions={isFloatingActions}>
            <Button
              ariaLabel={cancelLabel}
              onPress={onCancel}
              variant='secondary'
              iconLeft={isWithArrows ? 'long-arrow-left' : undefined}
            >
              {cancelLabel}
            </Button>
            <Button
              type='submit'
              ariaLabel={saveLabel}
              // onPress={onSave}
              iconRight={isWithArrows ? 'long-arrow-right' : undefined}
              isDisabled={isSaveDisabled}
            >
              {saveLabel}
            </Button>
          </EditorActions>
        )}
      </>
    ),
    [
      fields,
      value,
      onChange,
      isFloatingActions,
      isActionsDisabled,
      isWithArrows,
      cancelLabel,
      saveLabel,
      onCancel,
    ],
  );

  return isForm ? (
    <StyledEditor onSubmit={handleSubmit}>{EditorComponent}</StyledEditor>
  ) : (
    <StyledEditorDiv>{EditorComponent}</StyledEditorDiv>
  );
};

const StyledEditor = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[32]};
  width: 100%;
`;

const StyledEditorDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[32]};
  width: 100%;
`;

interface EditorActionsProps {
  $isFloatingActions?: boolean;
}
const EditorActions = styled.div<EditorActionsProps>`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.theme.spacing[16]};
  width: 100%;

  ${(props) =>
    !props.$isFloatingActions &&
    css`
      & > button {
        flex: 1;
      }
    `};
  ${(props) =>
    props.$isFloatingActions &&
    css`
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      gap: ${props.theme.spacing[16]};
      padding: ${props.theme.spacing[16]} 0;
      border-top: 1px solid ${props.theme.color.neutral[50]};
      background: ${props.theme.color.neutral[10]};
    `}
`;

const ActionButtonsPlaceholder = styled.div`
  width: 100%;
  height: 75px;
  min-height: 75px;
  max-height: 75px;
`;

interface EditorProps<T = any> {
  onSave?: () => void;
  onCancel?: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  isWithArrows?: boolean;
  isActionsDisabled?: boolean;
  value: T;
  onChange: (value: T, key: string) => void;
  fields: { [key in keyof T]: FieldDef };
  isFloatingActions?: boolean;
  isForm?: boolean;
  isDisabled?: boolean;
  isSaveDisabled?: boolean;
}

Editor.displayName = 'Editor';

export default Editor;
